import './App.css'
import React from 'react'
import { BrowserRouter as Router } from 'react-router-dom'

import CssBaseline from '@material-ui/core/CssBaseline'
import { createTheme, ThemeProvider } from '@material-ui/core/styles'
import CircularProgress from '@material-ui/core/CircularProgress'
import Backdrop from '@material-ui/core/Backdrop'

import { ResolutionProvider } from '@graniot/common/context/ResolutionContext'
import { LayersProvider } from '@graniot/common/context/LayersContext'
import { LocationProvider } from '@graniot/common/context/LocationContext'

import { useAuth } from '@graniot/common/context/AuthContext'
import { CompanyProvider } from '@graniot/common/context/CompanyContext'
import { MetadataVisibilityProvider } from '@graniot/common/context/MetadataVisibilityContext'
import { PostHogProvider } from 'posthog-js/react'

const options = {
  api_host: process.env.REACT_APP_POSTHOG_HOST,
  defaults: '2025-05-24',
  disable_session_recording: true, // Disabled by default, controlled programmatically
}

// Analytics only runs in production; dev and staging builds never load PostHog.
const posthogEnabled =
  process.env.REACT_APP_PROD === 'true' && Boolean(process.env.REACT_APP_POSTHOG_KEY)

const AuthenticatedApp = React.lazy(() => import('./AuthenticatedApp'))
const UnauthenticatedApp = React.lazy(() => import('./UnauthenticatedApp'))

const outerTheme = createTheme({
    palette: {
        primary: {
            main: '#232C57',
            light: '#4a5483',
            dark: '#1a2145',
        },
        secondary: {
            main: '#DB491B',
        },
    },
    typography: {
        fontFamily: ['Roboto', 'Fira Sans'].join(','),
    },
})

function App() {
    const { user } = useAuth()

    return (
        <ThemeProvider theme={outerTheme}>
            <CssBaseline />
            <Router>
                <React.Suspense
                    fallback={
                        <Backdrop open={true}>
                            <CircularProgress color="inherit" />
                        </Backdrop>
                    }
                >
                    {user ? (
                        <MetadataVisibilityProvider>
                            <LocationProvider>
                                <LayersProvider>
                                    <ResolutionProvider>
                                        <CompanyProvider>
                                            {posthogEnabled ? (
                                                <PostHogProvider apiKey={process.env.REACT_APP_POSTHOG_KEY} options={options}>
                                                    <AuthenticatedApp key={user.username} />
                                                </PostHogProvider>
                                            ) : (
                                                <AuthenticatedApp key={user.username} />
                                            )}
                                        </CompanyProvider>
                                    </ResolutionProvider>
                                </LayersProvider>
                            </LocationProvider>
                        </MetadataVisibilityProvider>
                    ) : (
                        <UnauthenticatedApp />
                    )}
                </React.Suspense>
            </Router>
        </ThemeProvider>
    )
}

export default App
